import { c as _c } from "react/compiler-runtime";
import { RechartsPie as Pie, RechartsSector as Sector, useRechartsChartHeight, useRechartsChartWidth, type RechartsPieSectorShapeProps as PieSectorShapeProps } from '@precis-digital/kurama/recharts';
import { useTheme } from '@precis-digital/kurama/utils';
import { cloneElement, isValidElement, type ReactElement } from 'react';
import type { HoverablePieDrawingProps, HoverablePieShapeProps } from '../../types';
import { getColorWithShadeOffset, getNumberValue } from '../../utils/chart';

/**
 * HoverablePieSectorShapeProps extends Recharts pie sector shape props with optional fillShadeOffset and strokeShadeOffset for hover styling.
 *
 * @property fillShadeOffset - Optional fill shade offset for hover.
 * @property strokeShadeOffset - Optional stroke shade offset for hover.
 * Extends {@link PieSectorShapeProps}.
 */
export type HoverablePieSectorShapeProps = PieSectorShapeProps & {
  fillShadeOffset?: number;
  strokeShadeOffset?: number;
};

/**
 * HoverablePieProps is the type used for pie/donut chart props with hover state.
 *
 * @property hoveredIndex - Optional index of the hovered sector.
 * @property shape - Optional custom shape or shape render function. Receives {@link HoverablePieSectorShapeProps}.
 * @property highlightProps - Optional props applied to the highlighted (hovered) sector. Type: {@link HoverablePieShapeProps}
 * @property activeProps - Optional props applied to active sectors. Type: {@link HoverablePieShapeProps}
 * @property inactiveProps - Optional props applied to inactive sectors. Type: {@link HoverablePieShapeProps}
 * Extends {@link PieProps} (omitting shape).
 */
type HoverablePieProps = HoverablePieDrawingProps & {
  hoveredIndex?: number | null;
};
const calculateRadius = ({
  dataPoint,
  baseRadius,
  maxRadius,
  radius,
  radiusOffset
}: {
  dataPoint: PieSectorShapeProps['payload'];
  baseRadius: number;
  maxRadius: number;
  radius: string | number | ((dataPoint: any) => number | string) | undefined;
  radiusOffset: string | number | ((dataPoint: any) => number | string) | undefined;
}): number => {
  if (typeof radius === 'function') {
    radius = radius(dataPoint);
  }
  if (typeof radius === 'string') {
    return Math.min(getNumberValue(radius, baseRadius), maxRadius);
  }
  if (typeof radius === 'number') {
    return radius;
  }
  if (typeof radiusOffset === 'function') {
    radiusOffset = radiusOffset(dataPoint);
  }
  if (typeof radiusOffset === 'string') {
    return Math.min(baseRadius + getNumberValue(radiusOffset, baseRadius), maxRadius);
  }
  if (typeof radiusOffset === 'number') {
    return baseRadius + radiusOffset;
  }
  return baseRadius;
};
const getMaxRadius = (width: number | undefined, height: number | undefined, cx: number | string, cy: number | string): number => {
  if (width == null || height == null) {
    return 0;
  }
  const centerX = getNumberValue(cx, width);
  const centerY = getNumberValue(cy, height);
  const maxToLeft = centerX;
  const maxToRight = width - centerX;
  const maxToTop = centerY;
  const maxToBottom = height - centerY;
  const maxRadius = Math.min(maxToLeft, maxToRight, maxToTop, maxToBottom);
  return maxRadius;
};
const getShapeProps = (shapeProps: PieSectorShapeProps, maxRadius: number, alterProps: HoverablePieShapeProps | undefined): HoverablePieSectorShapeProps => {
  if (alterProps == null) {
    return shapeProps;
  }
  const {
    outerRadiusOffset,
    innerRadiusOffset,
    innerRadius,
    outerRadius,
    fillOpacity,
    strokeWidth,
    strokeOpacity,
    cornerRadius,
    fill,
    fillShadeOffset,
    stroke,
    strokeShadeOffset
  } = alterProps;
  const {
    payload
  } = shapeProps;
  return {
    ...shapeProps,
    outerRadius: calculateRadius({
      dataPoint: payload,
      baseRadius: shapeProps.outerRadius,
      maxRadius,
      radius: outerRadius,
      radiusOffset: outerRadiusOffset
    }),
    innerRadius: calculateRadius({
      dataPoint: payload,
      baseRadius: shapeProps.innerRadius,
      maxRadius,
      radius: innerRadius,
      radiusOffset: innerRadiusOffset
    }),
    fill: fill ?? shapeProps.fill,
    fillShadeOffset,
    fillOpacity: fillOpacity ?? shapeProps.fillOpacity,
    stroke: stroke ?? shapeProps.stroke,
    strokeShadeOffset,
    strokeWidth: strokeWidth ?? shapeProps.strokeWidth,
    strokeOpacity: strokeOpacity ?? shapeProps.strokeOpacity,
    cornerRadius: (typeof cornerRadius === 'string' ? Number.parseFloat(cornerRadius) : cornerRadius) ?? shapeProps.cornerRadius
  };
};
const getVectorElement = (shapeProp: Exclude<HoverablePieProps['shape'], null | false>, hoverableShapeProps: HoverablePieSectorShapeProps, index: number, color: string | undefined, mode: 'light' | 'dark'): ReactElement => {
  const {
    fillShadeOffset,
    strokeShadeOffset,
    ...otherShapeProps
  } = hoverableShapeProps;
  const shapeProps: PieSectorShapeProps = {
    ...otherShapeProps,
    fill: getColorWithShadeOffset(color, fillShadeOffset, mode)
  };
  if (shapeProp == null) {
    return <Sector {...shapeProps} />;
  }
  if (typeof shapeProp === 'function') {
    return shapeProp(shapeProps, index);
  }
  if (isValidElement(shapeProp)) {
    return cloneElement(shapeProp, shapeProps);
  }
  if (typeof shapeProp === 'object') {
    const mergedProps: PieSectorShapeProps = Object.assign({}, shapeProps, shapeProp);
    return <Sector {...mergedProps} />;
  }
  return <Sector {...shapeProps} />;
};
const HoverablePie = t0 => {
  const $ = _c(24);
  let activeProps;
  let colorMap;
  let highlightProps;
  let hoveredIndex;
  let inactiveProps;
  let props;
  let shapeProp;
  if ($[0] !== t0) {
    ({
      hoveredIndex,
      shape: shapeProp,
      activeProps,
      highlightProps,
      inactiveProps,
      colorMap,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = activeProps;
    $[2] = colorMap;
    $[3] = highlightProps;
    $[4] = hoveredIndex;
    $[5] = inactiveProps;
    $[6] = props;
    $[7] = shapeProp;
  } else {
    activeProps = $[1];
    colorMap = $[2];
    highlightProps = $[3];
    hoveredIndex = $[4];
    inactiveProps = $[5];
    props = $[6];
    shapeProp = $[7];
  }
  const theme = useTheme();
  const chartWidth = useRechartsChartWidth();
  const chartHeight = useRechartsChartHeight();
  if (shapeProp === null || shapeProp === false) {
    let t1;
    if ($[8] !== props || $[9] !== shapeProp) {
      t1 = <Pie {...props} shape={shapeProp} />;
      $[8] = props;
      $[9] = shapeProp;
      $[10] = t1;
    } else {
      t1 = $[10];
    }
    return t1;
  }
  let t1;
  if ($[11] !== activeProps || $[12] !== chartHeight || $[13] !== chartWidth || $[14] !== colorMap || $[15] !== highlightProps || $[16] !== hoveredIndex || $[17] !== inactiveProps || $[18] !== shapeProp || $[19] !== theme.mode) {
    t1 = (shapeProps, index) => {
      const {
        name
      } = shapeProps;
      const color = (name == null ? undefined : colorMap?.[name]) ?? shapeProps.fill;
      if (hoveredIndex == null || activeProps == null && highlightProps == null && inactiveProps == null) {
        return getVectorElement(shapeProp, shapeProps, index, color, theme.mode);
      }
      const maxRadius = getMaxRadius(chartWidth, chartHeight, shapeProps.cx, shapeProps.cy);
      if (shapeProps.isActive && (activeProps != null || highlightProps != null)) {
        const activeShapeProps = getShapeProps(shapeProps, maxRadius, activeProps);
        if (highlightProps == null) {
          return getVectorElement(shapeProp, activeShapeProps, index, color, theme.mode);
        }
        const highlightShapeProps = getShapeProps(shapeProps, maxRadius, highlightProps);
        return <g>{getVectorElement(shapeProp, highlightShapeProps, index, color, theme.mode)}{getVectorElement(shapeProp, activeShapeProps, index, color, theme.mode)}</g>;
      }
      return getVectorElement(shapeProp, getShapeProps(shapeProps, maxRadius, inactiveProps), index, color, theme.mode);
    };
    $[11] = activeProps;
    $[12] = chartHeight;
    $[13] = chartWidth;
    $[14] = colorMap;
    $[15] = highlightProps;
    $[16] = hoveredIndex;
    $[17] = inactiveProps;
    $[18] = shapeProp;
    $[19] = theme.mode;
    $[20] = t1;
  } else {
    t1 = $[20];
  }
  const shape = t1;
  let t2;
  if ($[21] !== props || $[22] !== shape) {
    t2 = <Pie {...props} shape={shape} />;
    $[21] = props;
    $[22] = shape;
    $[23] = t2;
  } else {
    t2 = $[23];
  }
  return t2;
};
export default HoverablePie;